// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
    Button, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, Typography
} from "@mui/material";
import { FunctionComponent, PropsWithChildren, ReactElement, useEffect, useState } from "react"
import { useIdleTimer } from "react-idle-timer";
import { useAuthContext } from "@asgardeo/auth-react";
import { SESSION_TIMEOUT, SESSION_TIMEOUT_PROMPT } from "../data/configs/spl-config";
import CircularLoading from "./Loading";

interface IdleDetectionHandlerPropsInterface {
}

const timeout = SESSION_TIMEOUT * 60000
const promptBeforeIdle = (SESSION_TIMEOUT - SESSION_TIMEOUT_PROMPT) * 60000

export const IdleDetectionHandler: FunctionComponent<PropsWithChildren<
    IdleDetectionHandlerPropsInterface>> = (): ReactElement => {

    const [open, setOpen] = useState<boolean>(false);
    const { signOut } = useAuthContext();
    const [openLoading, setOpenLoading] = useState(false);
    const [remaining, setRemaining] = useState<number>(timeout)

    const onIdle = () => {
        setOpen(false);
        setOpenLoading(true);
        signOut();
    }

    const onActive = () => {
        setOpen(false);
    }

    const onPrompt = () => {
        setOpen(true);
    }

    const { getRemainingTime, activate } = useIdleTimer({
        onIdle,
        onPrompt,
        onActive,
        timeout,
        promptBeforeIdle,
        throttle: 500
    })

    const handleStillHere = () => {
        activate()
    }

    const handleLogOut = () => {
        setOpen(false);
        setOpenLoading(true);
        signOut();
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setRemaining(Math.ceil(getRemainingTime() / 60000))
        }, 30000)
        return () => {
            clearInterval(interval)
        }
    })

    return (
        <>
            <CircularLoading openLoading={openLoading} />
            <Dialog
                open={open}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Do you want to stay logged in?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {remaining == 1 &&
                            (<Typography paddingBottom={1}>
                                Logging out in {remaining} minute.
                            </Typography>)
                        }
                        {remaining > 1 &&
                            (<Typography paddingBottom={1}>
                                Logging out in {remaining} minutes.
                            </Typography>)
                        }
                        Your session is about to expire. You'll be automatically logged out.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogOut}>Log out now</Button>
                    <Button onClick={handleStillHere} autoFocus>
                        Im still here
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
